/**
 * DevTools routes: `/` and `/login` are public; factory + team surfaces sit
 * behind ProtectedRoute + Layout (Google → member JWT). Console home is `/home`.
 */

import { lazy, Suspense } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { routes } from './utils/navigation';

const LandingPage = lazy(() =>
  import('./pages/LandingPage').then((m) => ({ default: m.LandingPage })),
);
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const WorkCodingPage = lazy(() =>
  import('./pages/WorkCodingPage').then((m) => ({ default: m.WorkCodingPage })),
);
const WorkPeerBoardPage = lazy(() =>
  import('./pages/WorkCodingPage').then((m) => ({ default: m.WorkPeerBoardPage })),
);
const BoardPage = lazy(() => import('./pages/BoardPage').then((m) => ({ default: m.BoardPage })));
const PlanPage = lazy(() => import('./pages/PlanPage').then((m) => ({ default: m.PlanPage })));
const DispatchPage = lazy(() => import('./pages/DispatchPage').then((m) => ({ default: m.DispatchPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const AutomationsListPage = lazy(() =>
  import('./pages/AutomationsListPage').then((m) => ({ default: m.AutomationsListPage })),
);
const AutomationDetailPage = lazy(() =>
  import('./pages/AutomationDetailPage').then((m) => ({ default: m.AutomationDetailPage })),
);
const EventsListPage = lazy(() =>
  import('./pages/EventsListPage').then((m) => ({ default: m.EventsListPage })),
);
const CodingAgentsListPage = lazy(() =>
  import('./pages/CodingAgentsListPage').then((m) => ({ default: m.CodingAgentsListPage })),
);
const ForAgentsPage = lazy(() =>
  import('./pages/ForAgentsPage').then((m) => ({ default: m.ForAgentsPage })),
);
const CodingAgentEditPage = lazy(() =>
  import('./pages/CodingAgentEditPage').then((m) => ({ default: m.CodingAgentEditPage })),
);
const BuiltinAgentsListPage = lazy(() =>
  import('./pages/BuiltinAgentsListPage').then((m) => ({ default: m.BuiltinAgentsListPage })),
);
const BuiltinAgentDetailPage = lazy(() =>
  import('./pages/BuiltinAgentDetailPage').then((m) => ({ default: m.BuiltinAgentDetailPage })),
);
const ConsolePage = lazy(() =>
  import('./pages/ConsolePage').then((m) => ({ default: m.ConsolePage })),
);
const CommsPage = lazy(() => import('./pages/CommsPage').then((m) => ({ default: m.CommsPage })));
const ConnectionsPage = lazy(() =>
  import('./pages/ConnectionsPage').then((m) => ({ default: m.ConnectionsPage })),
);
const StatusPage = lazy(() =>
  import('./pages/StatusPage').then((m) => ({ default: m.StatusPage })),
);
const StatusConfigPage = lazy(() =>
  import('./pages/StatusConfigPage').then((m) => ({ default: m.StatusConfigPage })),
);
const RoadmapPage = lazy(() =>
  import('./pages/RoadmapPage').then((m) => ({ default: m.RoadmapPage })),
);
const StoriesPage = lazy(() =>
  import('./pages/StoriesPage').then((m) => ({ default: m.StoriesPage })),
);
const DeploymentsPage = lazy(() =>
  import('./pages/DeploymentsPage').then((m) => ({ default: m.DeploymentsPage })),
);
const TeamMembersPage = lazy(() =>
  import('./pages/TeamMembersPage').then((m) => ({ default: m.TeamMembersPage })),
);
const TeamProjectsPage = lazy(() =>
  import('./pages/TeamProjectsPage').then((m) => ({ default: m.TeamProjectsPage })),
);

/** Older board links used `/review`. That path is the diff tab. */
function ReviewRedirect() {
  const { id = '' } = useParams();
  return <Navigate to={routes.diff(id)} replace />;
}

function AppRoutes() {
  const { t } = useTranslation();

  return (
    <Suspense fallback={<p className="p-7 text-sm text-[var(--muted)]">{t('common.loading')}</p>}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        {/* Public: agent assimilation (llms.txt / skill index are static under public/). */}
        <Route path="/for-agents" element={<ForAgentsPage />} />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/home" element={<HomePage />} />

          {/* Work boards + in-page filters */}
          <Route path="/work/coding" element={<WorkCodingPage />} />
          <Route path="/work/support" element={<WorkPeerBoardPage view="support" />} />
          <Route path="/work/bugs" element={<WorkPeerBoardPage view="bugs" />} />

          {/* Legacy board deep links → Work */}
          <Route path="/wip" element={<Navigate to={routes.wip()} replace />} />
          <Route path="/backlog" element={<Navigate to={routes.backlog()} replace />} />
          <Route path="/all" element={<Navigate to={routes.all()} replace />} />
          <Route path="/done" element={<Navigate to={routes.done()} replace />} />
          <Route path="/open-prs" element={<Navigate to={routes.openPrs()} replace />} />
          <Route path="/pipelines" element={<Navigate to={routes.pipelines()} replace />} />
          <Route
            path="/failed-pipelines"
            element={<Navigate to={routes.failedPipelines()} replace />}
          />
          <Route path="/support" element={<Navigate to={routes.support()} replace />} />
          <Route path="/bugs" element={<Navigate to={routes.bugs()} replace />} />
          <Route
            path="/support/settings"
            element={<Navigate to={routes.supportSettings()} replace />}
          />

          <Route path="/dispatch" element={<DispatchPage />} />
          <Route path="/roadmap" element={<RoadmapPage />} />
          <Route path="/roadmap/initiatives/:initiativeId" element={<RoadmapPage />} />
          <Route path="/deployments" element={<DeploymentsPage />} />
          <Route path="/stories" element={<StoriesPage />} />
          <Route path="/stories/:projectId" element={<StoriesPage />} />
          <Route path="/stories/:projectId/documents/:documentId" element={<StoriesPage />} />

          <Route path="/automations" element={<AutomationsListPage />} />
          <Route path="/automations/new" element={<AutomationDetailPage />} />
          <Route path="/automations/:id" element={<AutomationDetailPage />} />
          <Route path="/automations/events" element={<EventsListPage />} />
          <Route path="/events" element={<Navigate to={routes.events()} replace />} />

          <Route path="/agents" element={<Navigate to={routes.agentsExternal()} replace />} />
          <Route path="/agents/external" element={<CodingAgentsListPage />} />
          <Route path="/agents/builtin" element={<BuiltinAgentsListPage />} />
          <Route path="/agents/builtin/:agentKey" element={<BuiltinAgentDetailPage />} />
          <Route path="/agents/new" element={<CodingAgentEditPage />} />
          <Route path="/agents/:id" element={<CodingAgentEditPage />} />
          <Route path="/coding-agents" element={<Navigate to={routes.agentsExternal()} replace />} />
          <Route path="/coding-agents/new" element={<Navigate to={routes.agentsExternalNew()} replace />} />
          <Route path="/coding-agents/:id" element={<CodingAgentLegacyRedirect />} />
          <Route path="/console" element={<ConsolePage />} />

          {/* More specific connection routes before :tab */}
          <Route path="/connections/git/new" element={<GitRepoLegacyRedirect />} />
          <Route path="/connections/git/:id" element={<GitRepoLegacyRedirect />} />
          <Route path="/connections" element={<Navigate to={routes.connections('git')} replace />} />
          <Route path="/connections/:tab" element={<ConnectionsPage />} />
          <Route path="/git-repos" element={<Navigate to={routes.connections('git')} replace />} />
          <Route path="/git-repos/new" element={<GitRepoLegacyRedirect />} />
          <Route path="/git-repos/:id" element={<GitRepoLegacyRedirect />} />

          <Route path="/status" element={<StatusPage />} />
          <Route path="/status/config" element={<StatusConfigPage />} />

          <Route path="/team" element={<Navigate to={routes.teamMembers()} replace />} />
          <Route path="/team/members" element={<TeamMembersPage />} />
          <Route path="/team/projects" element={<TeamProjectsPage />} />
          <Route path="/team/projects/:id" element={<TeamProjectsPage />} />

          <Route path="/comms" element={<CommsPage />} />
          <Route path="/comms/:conversationId" element={<CommsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/epics/:epicId" element={<BoardPage />} />
          {/* Bare /plans/:id → explicit Plan tab; other tabs keep their suffixes. */}
          <Route path="/plans/:id" element={<PlanTabRedirect />} />
          <Route path="/plans/:id/plan" element={<PlanPage />} />
          <Route path="/plans/:id/diff" element={<PlanPage />} />
          <Route path="/plans/:id/pipeline" element={<PlanPage />} />
          <Route path="/plans/:id/history" element={<PlanPage />} />
          <Route path="/plans/:id/artifacts" element={<PlanPage />} />
          <Route path="/plans/:id/artifacts/:artifactId" element={<PlanPage />} />
          <Route path="/plans/:id/review" element={<ReviewRedirect />} />
          <Route path="*" element={<Navigate to={routes.home()} replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

/** `/plans/:id` → `/plans/:id/plan` so the Plan tab has an explicit path. */
function PlanTabRedirect() {
  const { id = '' } = useParams();
  return <Navigate to={routes.plan(id)} replace />;
}

/** /coding-agents/:id → External list edit modal (or create for "new"). */
function CodingAgentLegacyRedirect() {
  const { id = '' } = useParams();
  if (!id || id === 'new') return <Navigate to={routes.agentsExternalNew()} replace />;
  return <Navigate to={routes.agentsExternalEdit(id)} replace />;
}

/** Legacy/full-page Git URLs → Connections list create/edit modal. */
function GitRepoLegacyRedirect() {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const git = searchParams.get('git') || undefined;
  if (!id || id === 'new') return <Navigate to={routes.gitRepoNew({ git })} replace />;
  return <Navigate to={routes.gitRepoEdit(id, { git })} replace />;
}

/** Root providers: theme tokens, then auth session, then route tree. */
export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}
